import * as React from "react"
import {
  type EntityCategory,
  type EntityTransaction,
  TransactionRecordType,
} from "@digits-graphql/frontend/graphql-bearer"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import {
  IconSize,
  IconSVGContainerNoBackgroundColor,
} from "@digits-shared/components/UI/Icons/Icon"
import { CellText } from "@digits-shared/DesignSystem/Tables/tableStyles"
import colors from "@digits-shared/themes/colors"
import styled from "styled-components"
import { CategoryIcon } from "src/frontend/components/Shared/Transactions/Icons/CategoryIcon"
import { EditableSplitCategoryCell } from "src/frontend/components/Shared/Transactions/TableCells/EditableCategoryCell"
import { isTransaction } from "src/frontend/components/Shared/Transactions/types"
import { useTransactionsUpdates } from "src/frontend/components/Shared/Transactions/Updates/TransactionsUpdatesContext"

const SplitContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  overflow-x: hidden;
  margin-left: 10px;
`

const TransactionCategoryIconStyled = styled(CategoryIcon)`
  display: inline-flex;
  margin-right: 5px;

  height: 18px;
  min-width: 18px;
  width: 18px;
  padding: 2px;

  ${IconSVGContainerNoBackgroundColor} {
    border-radius: 5px;
    background: transparent;

    svg {
      ${svgPathStyles(colors.secondary70, 1.5)};
    }
  }
`

export const SplitCategoryCell: React.FC<{ transaction: EntityTransaction }> = ({
  transaction,
}) => {
  const category = transaction.splitCategory
  const { allowInlineUpdates, getTransactionUpdate, updateTransaction } = useTransactionsUpdates()
  const updatedSplitCategory = getTransactionUpdate(transaction.factId)?.splitCategory

  const onCategorySelected = React.useCallback(
    (splitCategory: EntityCategory) => {
      if (!transaction.factId || !isTransaction(transaction)) return
      updateTransaction({ transaction, splitCategory })
    },
    [transaction, updateTransaction]
  )

  if (!category?.name) {
    return (
      <SplitContainer>
        <TransactionCategoryIconStyled
          category={{
            name: category?.name,
            displayKey: category?.displayKey || "Tags",
          }}
          iconSize={IconSize.SmallMedium}
          recordType={TransactionRecordType.Transaction}
        />
        <CellText>{category?.name || "Multiple Categories"}</CellText>
      </SplitContainer>
    )
  }

  return (
    <EditableSplitCategoryCell
      transaction={transaction}
      updatedCategory={updatedSplitCategory}
      onCategorySelected={onCategorySelected}
      closedTimestamp={undefined}
      editable={allowInlineUpdates}
    />
  )
}
